import { datadogRum } from "@datadog/browser-rum";
import { Box } from "@mui/material";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import React, { useEffect } from "react";
import { showBottomAndTopNavAtom } from "../../../atoms/navAtoms";
import { AuthFlows, AuthSteps } from "../../../constants/authSteps";
import { useMediaQueryBreakpoint } from "../../../hooks/useMediaQuery";
import useModal from "../../../hooks/useModal";
import { BaseModal } from "../../core-ui/components/BaseModal/BaseModal";
import { AccountCreateComplete } from "./AccountCreateComplete";
import { AddPassword } from "./AddPassword";
import { AddPhone } from "./AddPhone";
import AuthBannerInfoWrapper from "./AuthBannerInfoWrapper";
import { AuthModalContainer } from "./AuthModal.styles";
import { BuildTeam } from "./BuildTeam";
import { EmailAuth } from "./EmailAuth";
import { LabelSelection } from "./LabelSelection";
import { ProfileType } from "./ProfileType";
import { StartSignIn } from "./StartSignIn";
import { StartSignUp } from "./StartSignUp";
import StudioContact from "./StudioContact";
import StudioLocation from "./StudioLocation";
import { UsernameSelection } from "./UsernameSelection";
import VerifyEmail from "./VerifyEmail";
import { VerifyPhone } from "./VerifyPhone";
import { currentSignUpStepAtom } from "../../../atoms/location/currentSignUpStepAtom";
import { readCurrentStepFromUrlAtom } from "../../../atoms/location/readCurrentStepFromUrlAtom";
import { readCurrentFlowFromUrl } from "../../../atoms/location/readCurrentFlowFromUrlAtom";
import { authModalOpenAtom } from "../../../atoms/location/authModalOpenAtom";
import { currentFlowAtom } from "../../../atoms/location/currentFlowAtom";
import { useAtomCallback } from "jotai/utils";

const stepToForm: Record<number, React.FC> = {
  [AuthSteps.START_SIGNUP]: StartSignUp,
  [AuthSteps.START_LOGIN]: StartSignIn,
  [AuthSteps.LOGIN]: EmailAuth,
  [AuthSteps.EMAIL_SIGNUP]: EmailAuth,
  [AuthSteps.PROFILE_TYPE]: ProfileType,
  [AuthSteps.USERNAME]: UsernameSelection,
  [AuthSteps.VERIFY_EMAIL]: VerifyEmail,
  [AuthSteps.ADD_PHONE]: AddPhone,
  [AuthSteps.VERIFY_PHONE]: VerifyPhone,
  [AuthSteps.ADD_STUDIO_LOCATION]: StudioLocation,
  [AuthSteps.ADD_STUDIO_CONTACT]: StudioContact,
  [AuthSteps.COMPLETE]: AccountCreateComplete,
  [AuthSteps.AANDR_LABEL_SELECTION]: LabelSelection,
  [AuthSteps.AANDR_USERNAME]: UsernameSelection,
  [AuthSteps.AANDR_ADD_PHONE]: AddPhone,
  [AuthSteps.AANDR_VERIFY_PHONE]: VerifyPhone,
  [AuthSteps.AANDR_BUILD_TEAM]: BuildTeam,
  [AuthSteps.ADD_PASSWORD]: AddPassword,
};

export const AuthModal = () => {
  const { toggleModal } = useModal();
  const step = useAtomValue(currentSignUpStepAtom);
  const flow = useAtomValue(currentFlowAtom);
  const stepFromUrl = useAtomValue(readCurrentStepFromUrlAtom);
  const flowFromUrl = useAtomValue(readCurrentFlowFromUrl);
  const [authModalOpen, setAuthModalOpen] = useAtom(authModalOpenAtom);
  const StepForm = stepToForm[step];
  const nonAuthBannerSteps = [
    AuthSteps.PROFILE_TYPE,
    AuthSteps.AANDR_LABEL_SELECTION,
    AuthSteps.COMPLETE,
  ];
  const isNonAuthBannerStep = nonAuthBannerSteps.includes(step);
  const { isDesktop } = useMediaQueryBreakpoint();
  const setShowNavState = useSetAtom(showBottomAndTopNavAtom);

  // TODO: Wait for reply from Datadog about how to fix getSessionReplayLink not being accurate
  // We want to record the session while the modal is open, and stop it when the modal closes if it wasn't recording before loading
  useEffect(() => {
    // const hadSessionRunning = datadogRum.getSessionReplayLink();
    // console.log(">> Auth Session recording", hadSessionRunning);
    // if (!hadSessionRunning) {
    datadogRum.startSessionReplayRecording();
    // }
    // return () => {
    //   if (!hadSessionRunning) {
    //     datadogRum.stopSessionReplayRecording();
    //   }
    // };
  }, []);

  useEffect(() => {
    setShowNavState(undefined);
    if (isDesktop) {
      setShowNavState(true);
      return;
    }
    if (
      step === AuthSteps.EMAIL_SIGNUP ||
      step === AuthSteps.LOGIN ||
      step === AuthSteps.START_SIGNUP ||
      step === AuthSteps.START_LOGIN ||
      flow === AuthFlows.PHONE_ADD ||
      flow === AuthFlows.PHONE_LOGIN ||
      flow === AuthFlows.PHONE_SIGNUP
    ) {
      setShowNavState(true);
      return;
    }
    setShowNavState(undefined);
  }, [isDesktop, setShowNavState, step]);

  const updateFlowAndStep = useAtomCallback((get, set) => {
    const urlFlow = get(readCurrentFlowFromUrl);
    const urlStep = get(readCurrentStepFromUrlAtom);
    set(currentFlowAtom, urlFlow, true);
    set(currentSignUpStepAtom, urlStep);
  });
  useEffect(() => {
    if (
      !StepForm ||
      stepFromUrl === AuthSteps.NONE ||
      flowFromUrl === AuthFlows.NONE
    ) {
      setAuthModalOpen(false);
    } else {
      updateFlowAndStep();
      if (!authModalOpen) setAuthModalOpen(true);
    }
  }, [StepForm, authModalOpen, flowFromUrl, setAuthModalOpen, stepFromUrl]);

  return !isDesktop && authModalOpen ? (
    <Box
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        ...(step === AuthSteps.COMPLETE
          ? { height: "100%", justifyContent: "center" }
          : {}),
      }}
    >
      {StepForm ? <StepForm /> : null}
    </Box>
  ) : (
    <BaseModal
      header=""
      open={authModalOpen}
      setOpen={toggleModal}
      showModalHeader={false}
      style={{
        maxWidth:
          isNonAuthBannerStep && step !== AuthSteps.COMPLETE
            ? "1224px"
            : "840px",
        boxShadow: "0px 2px 30px 0px rgba(0, 0, 0, 0.15)",
        maxHeight: "90vh",
        overflow: "auto",
      }}
      modalBodyStyle={{ padding: 0, width: "100%" }}
    >
      <AuthModalContainer>
        <AuthBannerInfoWrapper />
        <Box
          style={{
            width: isNonAuthBannerStep ? "100%" : "50%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {StepForm ? <StepForm /> : null}
        </Box>
      </AuthModalContainer>
    </BaseModal>
  );
};

export default AuthModal;
